import Link from "next/link";
import React from "react";
import styled from "styled-components";
import { homepageRoute } from "~/core/routes";
import EncoreMieuxSigle, { Svg } from "~/guidelines/Logo/EncoreMieuxSigle";
import mediaQueries from "~/guidelines/Theme/mediaQueries";

type BackgroundShade = "lightFirst" | "lightSecond" | "none";

const HeaderContainer = styled.header<{
  backgroundShade: BackgroundShade;
  shadow?: boolean;
}>`
  position: sticky;
  top: 0;
  z-index: 100;
  width: 100%;
  display: grid;
  align-items: center;
  grid-template-columns: auto 1fr auto;
  gap: ${({ theme }) => theme.spacing(6)};
  padding: ${({ theme }) => theme.spacing(4, 6)};
  box-shadow: ${({ shadow, theme }) =>
    shadow ? theme.shadow.navigation : "none"};

  background-color: ${({ theme, backgroundShade }) =>
    backgroundShade === "none"
      ? "transparent"
      : theme.color.background[backgroundShade]};
  ${mediaQueries.md()} {
    padding: ${({ theme }) => theme.spacing(5, 6)};
  }
`;

const LogoArea = styled.div<{ hiddenOnMobile: boolean }>`
  min-width: 45px;
  display: ${({ hiddenOnMobile }) => (hiddenOnMobile ? "none" : "flex")};
  align-items: center;

  ${mediaQueries.md()} {
    display: flex;
  }

  & ${Svg} {
    height: 40px;
    width: 28px;

    color: ${({ theme }) => theme.color.text.lightFirst};

    ${mediaQueries.md()} {
      height: 48px;
      width: 33px;
    }
  }
`;

const StyledLink = styled(Link)`
  display: flex;
`;

const ActionsArea = styled.div`
  min-width: 45px;

  & > div {
    display: none;

    ${mediaQueries.md()} {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-end;
      flex-shrink: 0;
      gap: ${({ theme }) => theme.spacing(8)};
    }
  }
`;

const CentralArea = styled.div<{
  alwaysDisplay: boolean;
  centerItems?: boolean;
}>`
  display: ${({ alwaysDisplay }) => (alwaysDisplay ? "flex" : "none")};

  ${mediaQueries.md()} {
    display: flex;
    flex-grow: 1;
    flex-direction: row;
  }
`;

export type HeaderProps = {
  leftElement?: React.ReactNode;
  backgroundShade?: BackgroundShade;
  logoHref?: string;
  centralArea?: React.ReactNode;
  alwaysDisplayCentralArea?: boolean;
  children?: React.ReactNode;
  shadow?: boolean;
  logoHiddenOnMobile?: boolean;
  className?: string;
};

const Header = ({
  leftElement,
  logoHref = homepageRoute(),
  logoHiddenOnMobile = false,
  backgroundShade = "none",
  shadow = false,
  centralArea,
  alwaysDisplayCentralArea = false,
  children,
  className,
}: HeaderProps) => {
  return (
    <HeaderContainer
      backgroundShade={backgroundShade}
      shadow={shadow}
      className={className}
    >
      <LogoArea hiddenOnMobile={logoHiddenOnMobile}>
        {leftElement ? (
          leftElement
        ) : (
          <StyledLink href={logoHref} passHref>
            <EncoreMieuxSigle />
          </StyledLink>
        )}
      </LogoArea>

      <CentralArea alwaysDisplay={alwaysDisplayCentralArea}>
        {centralArea}
      </CentralArea>
      <ActionsArea>
        <div>{children}</div>
      </ActionsArea>
    </HeaderContainer>
  );
};

export default Header;
